import { Howl } from 'howler'
import { musicManager } from '../music/musicManager'

// One Howl per sound effect, built once at preload and reused for the life of
// the page. Howler pools voices internally, so overlapping plays of the same
// effect just spawn another voice — no cloning needed.
//
// Constructing these at startup matters beyond preloading: Howler binds its
// capture-phase touchstart/touchend/click/keydown unlock handlers when the
// FIRST Howl is created. Preloading on mount therefore arms the unlock before
// the player's first tap, which is what lets the deferred music autoplay flush.
//
// Muting is a play-time gate rather than a transport change; the effects are
// sub-second one-shots, so nothing is left hanging. Notably there is no
// pauseAll() and no visibilitychange wiring — see musicManager for why pausing
// anything on this shared AudioContext breaks audio permanently on iOS.

class SoundManager {
  constructor() {
    this.sounds = {}
    this.enabled = true
    this.volume = 0.8
  }

  load(name, src) {
    if (this.sounds[name]) return

    this.sounds[name] = new Howl({ src: [src], volume: this.volume })
  }

  play(name, options = {}) {
    if (!this.enabled) return

    const sound = this.sounds[name]
    if (!sound) return

    const id = sound.play()

    sound.volume(options.volume ?? this.volume, id)
    // Slight pitch jitter so repeated clicks don't sound mechanical.
    sound.rate(0.95 + Math.random() * 0.1, id)

    musicManager.duck()
  }

  setEnabled(value) {
    this.enabled = value
  }

  setVolume(value) {
    this.volume = value

    Object.values(this.sounds).forEach((sound) => sound.volume(value))
  }
}

export const soundManager = new SoundManager()
